import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SparkleIcon as Sparkle, XIcon as X, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Smart "next step" banner driven by the user's actual activity.
 * Renders the highest-priority unmet item from `/api/dashboard/next-step`.
 *
 * - The backend computes priority — frontend just renders whatever
 *   comes back. Keeps the ladder ordering server-side so we can
 *   A/B test or reorder without a frontend rebuild.
 * - Dismissal hits `/api/dashboard/next-step/dismiss/{id}` with a
 *   30-day server-side snooze. After dismissal, we optimistically
 *   advance to the next candidate by re-fetching.
 * - Returns `null` (no DOM) when there's nothing to suggest (power
 *   user, admin, or every relevant nudge has been dismissed). Falls
 *   silent on network failure so the dashboard never breaks because
 *   of a non-essential suggestion strip.
 *
 * `tone` from the API:
 *   - `gold` — monetization upsell (gold gradient accent)
 *   - `ink`  — onboarding / activation step (dark accent)
 */
export default function NextStepNudge() {
  const [nudge, setNudge] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get("/dashboard/next-step")
      .then((r) => setNudge(r.data?.nudge || null))
      .catch(() => setNudge(null));
  };

  useEffect(() => { load(); }, []);

  // Stay out of the persona dialog's way — the modal covers this strip
  // anyway, and its own "pick your role" nudge would be redundant noise.
  const [personaDialogOpen, setPersonaDialogOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setPersonaDialogOpen(true);
    const onDone = () => { setPersonaDialogOpen(false); load(); };
    window.addEventListener("persona-dialog-open", onOpen);
    window.addEventListener("persona-changed", onDone);
    window.addEventListener("persona-skipped", onDone);
    return () => {
      window.removeEventListener("persona-dialog-open", onOpen);
      window.removeEventListener("persona-changed", onDone);
      window.removeEventListener("persona-skipped", onDone);
    };
  }, []);

  if (!nudge || personaDialogOpen) return null;

  const isGold = nudge.tone === "gold";

  const dismiss = async () => {
    setBusy(true);
    const dismissed = nudge.id;
    setNudge(null); // optimistic hide
    try {
      await api.post(`/dashboard/next-step/dismiss/${dismissed}`);
    } catch {
      // Server failed — re-show so user can retry; not worth a toast.
      load();
    } finally {
      setBusy(false);
      // After dismissal, fetch the next-priority candidate.
      setTimeout(load, 200);
    }
  };

  const goNext = () => {
    if (nudge.cta_route) navigate(nudge.cta_route);
  };

  return (
    <div
      className={`relative rounded-2xl border-2 mb-8 overflow-hidden ${
        isGold
          ? "border-gold bg-gradient-to-br from-[#FBF7EC] via-[#FDF8E4] to-white shadow-[0_18px_44px_rgba(201,162,39,0.18)]"
          : "border-ink/30 bg-gradient-to-br from-cream via-white to-cream"
      }`}
      data-testid={`next-step-nudge-${nudge.id}`}
    >
      <div className="p-5 sm:p-6 flex items-start gap-4 flex-wrap">
        <div className={`flex-shrink-0 h-11 w-11 rounded-full flex items-center justify-center ${
          isGold ? "bg-gold-gradient text-ink" : "bg-ink text-gold-light"
        }`}>
          <Sparkle size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <p className={`text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5 ${
            isGold ? "text-gold-dark" : "text-ink/60"
          }`}>
            {nudge.hat_label ? `Next step for ${nudge.hat_label}` : "Recommended next step"}
          </p>
          <h3 className="font-spectral text-xl sm:text-2xl text-ink leading-tight" data-testid="next-step-nudge-title">
            {nudge.title}
          </h3>
          <p className="text-[13px] sm:text-[13.5px] text-[#3a3a3a] mt-1.5 leading-relaxed max-w-2xl">
            {nudge.body}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:ml-auto sm:flex-shrink-0">
          <Button
            onClick={goNext}
            className={`rounded-full h-11 px-5 font-semibold ${
              isGold
                ? "bg-gold-gradient text-ink hover:opacity-90 border-0 shadow-[0_8px_22px_rgba(201,162,39,0.28)]"
                : "bg-ink hover:bg-crimson text-white"
            }`}
            data-testid="next-step-nudge-cta"
          >
            {nudge.cta_label || "Get started →"}
            <ArrowRight size={14} weight="bold" className="ml-1" />
          </Button>
          <button
            onClick={dismiss}
            disabled={busy}
            className="text-[11px] text-[#9A9A9A] hover:text-ink underline-offset-2 hover:underline self-center disabled:opacity-50"
            data-testid="next-step-nudge-dismiss"
          >
            Hide for 30 days
          </button>
        </div>
        <button
          onClick={dismiss}
          disabled={busy}
          aria-label="Dismiss"
          className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full hover:bg-black/5 flex items-center justify-center text-[#9A9A9A] hover:text-ink transition-colors"
          data-testid="next-step-nudge-x"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
